import { apiClient } from '@littlebird/auth-client';
import { EventNames, globalEventBus } from '@littlebird/event-bus';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { useOpenChat } from '@/features/chat/use-open-chat';
import { electronApis } from '@/features/electron-api';
import { useRoutineReportNotifier } from '@/features/routines/routine-report-notifier';
import { inBrowser, isMac } from '@/utils/capabilities';

interface PushNotificationPayload {
  title: string;
  body: string;
  url?: string;
  type?: string;
}

interface WebSocketPushNotification {
  title: string;
  body: string;
  data?: Record<string, string> | null;
}

const REPORT_CARD_TIMEOUT_MS = 15_000;

export const notifyRoutineReportWithin = async (
  reportId: string,
  notifyFromPush: (reportId: string, signal?: AbortSignal) => Promise<boolean>,
  timeoutMs = REPORT_CARD_TIMEOUT_MS
): Promise<boolean> => {
  const abortController = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<boolean>((resolve) => {
    timeoutId = setTimeout(() => {
      abortController.abort();
      resolve(false);
    }, timeoutMs);
  });

  try {
    return await Promise.race([
      notifyFromPush(reportId, abortController.signal),
      timedOut,
    ]);
  } finally {
    clearTimeout(timeoutId);
  }
};

/** Whether the push is a routine run reaching its user, which waits on them until they act on it */
const waitsOnTheUser = (data: WebSocketPushNotification['data']): boolean =>
  Boolean(data?.reach_out);

/** The chat a routine run asked its user something in, which a reply belongs in */
const getReachOutReplyChatId = (
  data: WebSocketPushNotification['data']
): string | null =>
  data?.reach_out === 'reply' && data.chat_id ? data.chat_id : null;

export function usePushNotifications() {
  const lastRegisteredToken = useRef<string | null>(null);
  const { notifyFromPush: notifyRoutineReportFromPush } =
    useRoutineReportNotifier();
  const { openChat } = useOpenChat();

  useEffect(() => {
    if (!isMac()) return;

    const registerToken = async (apnsToken: string) => {
      if (lastRegisteredToken.current === apnsToken) return;
      try {
        const [deviceId, deviceSlug] = await Promise.all([
          electronApis.system.getDeviceId(),
          electronApis.system.getDeviceSlug(),
        ]);
        lastRegisteredToken.current = apnsToken;
        await apiClient.post('push/register', {
          device_id: deviceId,
          push_token: apnsToken,
          platform: 'macos',
          device_slug: deviceSlug,
        });
      } catch {
        lastRegisteredToken.current = null;
      }
    };

    const tokenHandler = (_event: unknown, token: string) => {
      registerToken(token);
    };
    const unsubscribe = window.ipcRenderer.on('apns-token-ready', tokenHandler);

    const checkExisting = async () => {
      const token = (await window.ipcRenderer.invoke('get-apns-token')) as
        | string
        | null;
      if (token) registerToken(token);
    };
    checkExisting();

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!isMac()) return;

    const handler = (_event: unknown, payload: PushNotificationPayload) => {
      electronApis.lbNotifications.native.sendNotification({
        id: crypto.randomUUID(),
        title: payload.title,
        message: payload.body,
        sound: 'attention',
        ...(payload.url && { route: { to: payload.url } }),
        notificationType: payload.type,
      });
    };

    return window.ipcRenderer.on('push-notification-received', handler);
  }, []);

  useEffect(() => {
    const showNativeNotification = (data: WebSocketPushNotification) => {
      const url = data.data?.url;
      electronApis.lbNotifications.native.sendNotification({
        id: crypto.randomUUID(),
        title: data.title,
        message: data.body,
        sound: 'attention',
        route: url ? { to: url } : undefined,
        notificationType: data.data?.type,
        // A run that reached its user waits on them, so its push stays until they act on it
        ...(waitsOnTheUser(data.data) && { autoDismiss: false }),
      });
    };

    const handlePushNotification = async (data: WebSocketPushNotification) => {
      const replyChatId = getReachOutReplyChatId(data.data);
      if (inBrowser()) {
        toast(data.title, {
          description: data.body,
          ...(waitsOnTheUser(data.data) && { duration: Infinity }),
          action: replyChatId
            ? { label: 'Reply', onClick: () => openChat(replyChatId) }
            : undefined,
        });
        return;
      }

      // A run that waits on its user has no report yet: its push says what it waits on
      const reportId = waitsOnTheUser(data.data)
        ? undefined
        : data.data?.report_id;
      if (
        reportId &&
        (await notifyRoutineReportWithin(reportId, notifyRoutineReportFromPush))
      ) {
        return;
      }

      showNativeNotification(data);
    };

    const subscription = globalEventBus.on(
      EventNames.PUSH_NOTIFICATION,
      ({ data }) => {
        void handlePushNotification(data);
      }
    );
    return () => {
      subscription.unsubscribe();
    };
  }, [notifyRoutineReportFromPush, openChat]);
}
